import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Button from 'components/common/Button/Button'
import PrintButton from 'components/common/PrintButton/PrintButton'
import { H1 } from 'components/common/Text/Text'
import QuoteSummary from 'components/common/QuoteSummary'
import RoundIcon from 'components/common/RoundIcon/RoundIcon'
import Columns from 'components/common/Columns/Columns'
import { injectProps } from 'helpers/utils'
import AppointmentSuccessModal from '../AppointmentSuccessModal'
import AppointmentChangeModal from '../AppointmentChangeModal'
import {
  SERVICES_SELECTOR_TARGET_ID,
  DATE_TIME_SELECTOR_TARGET_ID,
  APPOINTMENT_SUMMARY_SELECTOR_TARGET_ID } from 'constant'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import TopStoreSelector from './components/TopStoreSelector'
import SaveForLater from './components/SaveForLater'
import PromoExpirationNotification from './components/PromoExpirationNotification'
import Spinner from 'components/common/Spinner'
import OrSeparator from 'components/common/OrSeparator/OrSeparator'
import { formatPhoneNumber } from 'helpers/phone-number'
import PhoneLink from 'components/common/PhoneLink'
import Icon from 'components/common/Icon/Icon'
import { DATEPICKER_THEME_COLOR_GREEN } from 'components/QuickAppointmentPage/ThreeStepper/constants'
import { REQUEST_APPOINTMENT_BUTTON, REQUEST_APPOINTMENT_BUTTON_SIDEBAR } from './constants'

const Checkout = ({
  appointmentEnable,
  isDateTimeSelected,
  appointmentSuccessMessage,
  quoteSummary,
  cartridgesMap,
  currentStore,
  handleScrollTo,
  isFormFilled,
  submitForm,
  isQuoteReady,
  stickyTop,
  showCallStorePromoExpirationNotification,
  showInRangePromoExpirationNotification,
  isAppointmentSchedulingInProgress,
  storePhoneNumber,
  ...props
}) => <>
  {isAppointmentSchedulingInProgress && <Spinner /> }
  { cartridgesMap.BOTQuoteBreadcrumbTwoColumnsLink }
  <HeadingWrapper id={ SERVICES_SELECTOR_TARGET_ID }>
    <Logo small inline asset="cart-red" />
    <Heading headline headline_narrow inline noMargin>Shopping Cart Quote</Heading>
  </HeadingWrapper>
  <TopStoreSelector quoteSummary={ quoteSummary } currentStore={ currentStore } />
  { cartridgesMap.BOTYourVehicle }
  <Columns adaptive width="70%">
    <div>
      <ProductTable>
        {cartridgesMap.BOTProductAndServicesQuote}
      </ProductTable>
      <PaymentOptionContainer>
        {cartridgesMap.BOTPaymentOptionsBanner}
      </PaymentOptionContainer>
      { isQuoteReady &&
      <Fragment>
        {injectProps(cartridgesMap.BOTDateTimeSelector, { isCheckout: true, theme: DATEPICKER_THEME_COLOR_GREEN })}

        {(showCallStorePromoExpirationNotification || showInRangePromoExpirationNotification)
              && <PromoExpirationNotification
                isCallStorePromoExpiration={ showCallStorePromoExpirationNotification } />}

        {cartridgesMap.BOTAppointmentSummary}
        {
          appointmentEnable && <ContactDetailsWrapper>
            {cartridgesMap.BOTAppointmentContactDetails}
            <Media.Desktop>
              {
                isFormFilled && isDateTimeSelected &&
                <StyledSubmitButton
                  className={ REQUEST_APPOINTMENT_BUTTON }
                  narrow
                  full
                  quotePage
                  { ...getOnClickAndOnKeyPressDownEvents(submitForm) }>
                  Request Appointment
                </StyledSubmitButton>
              }
            </Media.Desktop>
            <Media.Mobile>
              {
                isDateTimeSelected &&
                <Fragment>
                  <StyledSubmitButton
                    className={ REQUEST_APPOINTMENT_BUTTON }
                    narrow
                    quotePage
                    stacked="mobile"
                    { ...getOnClickAndOnKeyPressDownEvents(submitForm) }>
                    Request Appointment
                  </StyledSubmitButton>
                  <StyledSubmitButton
                    stacked="mobile"
                    secondary
                    narrow
                    quotePageReverted
                    { ...getOnClickAndOnKeyPressDownEvents(
                      () => handleScrollTo(APPOINTMENT_SUMMARY_SELECTOR_TARGET_ID, 40)) }>
                    Edit Appointment
                  </StyledSubmitButton>
                </Fragment>
              }
              {
                !isDateTimeSelected &&
                <SubmitButton
                  narrow
                  stacked="mobile"
                  quotePage
                  { ...getOnClickAndOnKeyPressDownEvents(
                    () => handleScrollTo(DATE_TIME_SELECTOR_TARGET_ID)) }>
                  Select Date And Time
                </SubmitButton>
              }
            </Media.Mobile>
            <Media.Mobile>
              <OrSeparatorComponent />
              <CallStoreText mobile>
                Call store for help or to schedule an appointment over the phone
              </CallStoreText>
              <PhoneLink number={ formatPhoneNumber(storePhoneNumber) } >
                <PhoneIcon asset="phone-blue" height={ 13 } width={ 13 } />
                {formatPhoneNumber(storePhoneNumber)}
              </PhoneLink>
            </Media.Mobile>
          </ContactDetailsWrapper>
        }
      </Fragment>
      }
    </div>
    <Media.Desktop>
      <QuoteSummaryWrapper style={{ top: stickyTop }}>
        <Summary
          { ...quoteSummary }
          currentStore={ currentStore || {} }
          showStoreSection
          isCheckout >
          { isQuoteReady &&
          <Media.Desktop>
            { appointmentEnable &&
            <Fragment>
              {
                !isDateTimeSelected &&
                <SubmitButton
                  full
                  narrow
                  quotePage
                  { ...getOnClickAndOnKeyPressDownEvents(
                    () => handleScrollTo(DATE_TIME_SELECTOR_TARGET_ID)) }>
                  Select Date And Time
                </SubmitButton>
              }
              {
                isDateTimeSelected && !isFormFilled &&
                <SubmitButton
                  narrow
                  full
                  quotePage
                  { ...getOnClickAndOnKeyPressDownEvents(submitForm) }>
                  Enter Contact Info
                </SubmitButton>
              }
              {
                isDateTimeSelected && isFormFilled &&
                <SubmitButton
                  className={ REQUEST_APPOINTMENT_BUTTON_SIDEBAR }
                  narrow
                  full
                  quotePage
                  { ...getOnClickAndOnKeyPressDownEvents(submitForm) }>
                  Request Appointment
                </SubmitButton>
              }
            </Fragment>
            }
            {
              !appointmentEnable &&
              <StyledPrintButton full data-printless button>
                Print Quote
              </StyledPrintButton>
            }
            <OrSeparatorComponent />
            <CallStoreText>
              Call store for help or to schedule an appointment over the phone
            </CallStoreText>
            <PhoneNumber>
              {formatPhoneNumber(storePhoneNumber)}
            </PhoneNumber>
          </Media.Desktop>
          }
        </Summary>
      </QuoteSummaryWrapper>
    </Media.Desktop>
  </Columns>
  <AppointmentSuccessModal message={ appointmentSuccessMessage } />
  <AppointmentChangeModal />
  <SaveForLater { ...props } />
</>

export default Checkout

Checkout.propTypes = {
  appointmentEnable: PropTypes.bool,
  appointmentSuccessMessage: PropTypes.string,
  cartridgesMap: PropTypes.shape(),
  currentStore: PropTypes.shape(),
  formInitialValues: PropTypes.shape(),
  handleScrollTo: PropTypes.func,
  isAppointmentSchedulingInProgress: PropTypes.bool,
  isDateTimeSelected: PropTypes.bool,
  isFormFilled: PropTypes.bool,
  isQuoteReady: PropTypes.bool,
  productsAndServicesDrawerExpanded: PropTypes.bool,
  quoteSummary: PropTypes.shape(),
  showCallStorePromoExpirationNotification: PropTypes.bool,
  showInRangePromoExpirationNotification: PropTypes.bool,
  stickyTop: PropTypes.string,
  storePhoneNumber: PropTypes.string,
  submitForm: PropTypes.func
}

const HeadingWrapper = styled.div`
  && {
    text-align: center;
    white-space: nowrap;
    ${Mixin.responsive('margin-bottom', '40px', '20px')}
    ${Mixin.responsive('margin-top', '0', '20px')}
  }
`

const Heading = styled(H1)`
  ${Mixin.responsive('font-size', '26px', '20px')}
`

const Logo = styled(RoundIcon)`
  color: red;
  vertical-align: middle;
  ${Mixin.responsive('margin-right', '15px', '10px')}
`

const ProductTable = styled.div`
  ${Media.mobile`
    margin: 0 16px;
  `}
`

const ContactDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SubmitButton = styled(Button)`
  margin-top: 15px;
  margin-bottom: 20px;
  ${Media.mobile`
      width: 90%;
  `}
`

const StyledSubmitButton = styled(SubmitButton)`
  width: 288px;
  ${Media.mobile`
      width: 90%;
  `}
`

const StyledPrintButton = styled(PrintButton)`
  margin-top: 15px;
  margin-bottom: 20px;
  width: 100%;
`

const QuoteSummaryWrapper = styled.div`
  ${Media.desktop`
    position: -webkit-sticky;
    position: sticky;
    top: ${props => props.isHeaderSticky ? '-55px' : '-2px'};
  `}
  ${Media.mobile`
    margin-top: 25px;
  `}
`

const Summary = styled(QuoteSummary)`
  ${Media.desktop`
    flex: 0 0 auto;
    padding-left: 30px;
    align-self: flex-start;
    margin-left: auto;
    width: 370px;
  `}
  ${Mixin.responsive('margin', '0 50px', '0 16px')}
`
const PaymentOptionContainer = styled.div`
  margin-top: 20px;
`

const OrSeparatorComponent = styled(OrSeparator)`
  margin-top: -15px;
`

const CallStoreText = styled.p`
  margin: 0 0 15px;
  text-align: center;
  font-size: 12px;
  ${props => props.mobile && css`
    max-width: 240px;
    font-size: 14px;
  `}
`

const PhoneNumber = styled.p`
  font-weight: 700;
  margin: 0;
  text-align: center;
`

const PhoneIcon = styled(Icon)`
  margin: 0 5px 0 0;
`
